import { v4 as uuidv4 } from "uuid";
import config, { MAX_CHARACTER_LIMIT } from "@/const/config";
import { SearchErrors } from "@/typings/ErrorStates";
import triggerAnalyticsEvent, {
  sendResultsToTealium,
} from "@/helpers/triggerAnalyticsEvent";
import isSessionStorageEnabled from "@/helpers/isSessionStorageEnabled";
import {
  getSessionStorage,
  setSessionStorage,
} from "@/helpers/handleSessionStorage";

const handleError = (error: SearchErrors) => {
  triggerAnalyticsEvent({
    PageName: error,
    pageKind: "error",
    ABTestname: "",
    domainidentifier: "Joyhunt",
  });
};

const getProducts = async (query: string) => {
  if (!query.length) {
    handleError(SearchErrors.Unknown);

    return {
      ok: false,
      data: null,
      error: SearchErrors.Unknown,
    };
  }

  if (isSessionStorageEnabled()) {
    const searchCount = getSessionStorage(`searchCount-${query}`);
    const cachedProducts = getSessionStorage(query);
    if (cachedProducts && searchCount && Number(searchCount) >= 3) {
      return { ok: true, data: JSON.parse(cachedProducts), error: null };
    }
  }

  if (query && query?.trim().length >= MAX_CHARACTER_LIMIT) {
    return {
      ok: false,
      data: null,
      error: SearchErrors.TooLong,
    };
  }

  try {
    let correlationId = "";

    if (navigator.cookieEnabled) {
      correlationId = sessionStorage.getItem("syfinc-correlationId") as string;

      if (!correlationId) {
        sessionStorage.setItem("syfinc-correlationId", uuidv4());
        correlationId = sessionStorage.getItem(
          "syfinc-correlationId"
        ) as string;
      }
    }

    const headers =
      process.env.NODE_ENV === "development"
        ? {
            sessionId: correlationId,
            "X-SYF-API-KEY": config.CLIENT_ID,
            "X-SYF-Request-TrackingId": "9701e727-87a2-4e75-9390-d8d44541f922",
          }
        : {
            sessionId: correlationId,
            "X-SYF-API-KEY": config.CLIENT_ID,
          };

    const response = await fetch(
      `${config.PRODUCT_SEARCH_URL}?userPrompt=${query}`,
      {
        headers: headers as HeadersInit,
      }
    );

    const responseJson = await response.json();

    if (
      response.status === 500 &&
      responseJson.fault?.detail?.errorCode ===
        "policies.ratelimit.QuotaViolation"
    ) {
      return {
        ok: false,
        data: null,
        error: SearchErrors.RateLimit,
      };
    } else if (
      response.status === 400 &&
      responseJson.message.includes("Moderation triggered")
    ) {
      return {
        ok: false,
        data: null,
        error: SearchErrors.Sensitive,
      };
    }
    if (!response.ok) {
      return {
        ok: false,
        data: null,
        error: SearchErrors.Unknown,
      };
    }

    if (!responseJson.length) {
      return {
        ok: false,
        data: null,
        error: SearchErrors.NeedDetails,
      };
    }

    if (isSessionStorageEnabled()) {
      setSessionStorage(query, JSON.stringify(responseJson));

      const count = getSessionStorage(`searchCount-${query}`) || 0;
      const incrementedCount = Number(count) + 1;
      setSessionStorage(`searchCount-${query}`, String(incrementedCount));
    }

    sendResultsToTealium(responseJson, query);

    return {
      ok: true,
      data: responseJson,
      error: null,
    };
  } catch (e) {
    console.error(e);
    return {
      ok: false,
      data: null,
      error: SearchErrors.Unknown,
    };
  }
};

export default getProducts;
